import { useReactQuery } from '@flik/shared/api';
import { useTheme } from '@flik/shared/components';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';

interface FormData {
  email: string;
  organizationName: string;
}

const GetStartedPage: React.FC = () => {
  const { provideTailwind } = useTheme();
  const navigate = useNavigate();
  const apiClient = useReactQuery();

  const [formData, setFormData] = useState<FormData>({
    email: '',
    organizationName: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create the mutation hook for requesting organization creation
  const requestCreateOrganization = apiClient.useMutation(
    'post',
    '/v1/people/request-create-organization',
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await requestCreateOrganization.mutateAsync({
        body: {
          creator_email: formData.email,
          data: {
            name: formData.organizationName,
          },
        },
      });

      if (
        response.message ===
        'Confirmation email sent. Please check your inbox to confirm organization creation.'
      ) {
        setIsSuccess(true);
      } else {
        setError(
          'Failed to request organization creation. Please try again later.',
        );
      }
    } catch (err) {
      console.error('Error requesting organization creation:', err);
      setError(
        'An error occurred while submitting your request. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (isSuccess) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <NavBar />

        <div className="max-w-2xl mx-auto px-8 py-16">
          <div className="text-center">
            {/* Success tick icon */}
            <div className="mx-auto w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h1
              className={`text-4xl font-bold mb-6 ${provideTailwind(
                'text-primary',
              )}`}
            >
              We're excited to have you with us!
            </h1>

            <p className={`text-xl mb-8 ${provideTailwind('text-secondary')}`}>
              Check your email for next steps to complete your organization
              setup.
            </p>

            <button
              onClick={handleBackToHome}
              className={`px-8 py-3 rounded-lg font-semibold transition-colors ${provideTailwind(
                'bg-secondary text-primary hover:bg-opacity-80',
              )}`}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
      <NavBar />

      <div className="max-w-2xl mx-auto px-8 py-16">
        <div className="text-center mb-12">
          <h1
            className={`text-4xl font-bold mb-6 ${provideTailwind(
              'text-primary',
            )}`}
          >
            Get Started with Flik
          </h1>
          <p className={`text-xl ${provideTailwind('text-secondary')}`}>
            Tell us a bit more about your organization
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className={`block text-sm font-medium mb-2 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 rounded-lg border ${provideTailwind(
                'bg-secondary border-gray-300 text-primary focus:border-blue-500 focus:ring-2 focus:ring-blue-200',
              )}`}
              placeholder="your.email@company.com"
            />
          </div>

          <div>
            <label
              htmlFor="organizationName"
              className={`block text-sm font-medium mb-2 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Organization *
            </label>
            <input
              type="text"
              id="organizationName"
              name="organizationName"
              value={formData.organizationName}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 rounded-lg border ${provideTailwind(
                'bg-secondary border-gray-300 text-primary focus:border-blue-500 focus:ring-2 focus:ring-blue-200',
              )}`}
              placeholder="Your company name"
            />
          </div>

          {error && (
            <div
              className={`p-4 rounded-lg ${provideTailwind(
                'bg-red-100 border border-red-300',
              )}`}
            >
              <p className={`text-sm ${provideTailwind('text-red-700')}`}>
                {error}
              </p>
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="button"
              onClick={handleBackToHome}
              className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${provideTailwind(
                'bg-gray-200 text-gray-700 hover:bg-gray-300',
              )}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isSubmitting || !formData.email || !formData.organizationName
              }
              className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                isSubmitting || !formData.email || !formData.organizationName
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Confirm'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GetStartedPage;
